/**
 * Turning what the ICE machinery reports into sentences somebody can act on.
 *
 * Kept apart from [`PeerLinks`] and free of any browser object, because the phrasing is
 * the whole point of it and phrasing is worth testing. Every function here takes plain
 * facts and returns a string; nothing here decides *whether* to say something, which is
 * the caller's business.
 *
 * The vocabulary is ICE's, reduced to the three cases that matter to a person:
 *
 * - **host** — an address of this machine. Works only between browsers on one network.
 * - **srflx** — "server-reflexive": what a STUN server said this browser looks like from
 *   the outside. Its presence is the proof that STUN answered at all.
 * - **relay** — an address on a TURN server, which carries the traffic. The only thing
 *   that gets through a symmetric NAT, and the thing this deployment does not run.
 */

import type { IceConfig } from './signalling.js';

/** A gathered candidate, reduced to the parts worth reading aloud. */
export interface CandidateFacts {
  readonly type: string | null;
  readonly protocol: string | null;
  readonly address: string | null;
  readonly port: number | null;
}

/** An `icecandidateerror` event, reduced likewise. */
export interface CandidateErrorFacts {
  readonly url: string;
  readonly errorCode: number;
  readonly errorText: string;
}

/** The pair of addresses a working connection settled on. */
export interface Route {
  readonly local: string;
  readonly remote: string;
}

/**
 * What the server offered to find routes with.
 *
 * The credential is deliberately absent and the username with it: a console line is a
 * thing people paste into bug reports, and neither is needed to tell whether the server
 * handed out what it should have.
 */
export function describeIceServers(ice: IceConfig): string {
  const credentials = ice.username === undefined ? 'without' : 'with';
  return `${ice.urls.join(', ')} (${credentials} credentials)`;
}

/** What the STUN server said, which is the answer to "is STUN working from here?". */
export function describeReflexive(candidate: CandidateFacts): string {
  const address = candidate.address ?? 'an address it did not disclose';
  const port = candidate.port === null ? '' : `:${candidate.port.toString()}`;
  const protocol = candidate.protocol ?? 'an unrecorded protocol';

  return `The STUN server answered: from outside, this browser looks like ${address}${port} over ${protocol}.`;
}

/**
 * Why a configured server could not be used.
 *
 * Worth a line of its own because nothing else reports it: a connection that gathered no
 * server-reflexive candidate behaves exactly as though the browser had no public address,
 * which is indistinguishable from an unlucky network unless the failure itself is shown.
 */
export function describeCandidateError(error: CandidateErrorFacts): string {
  const detail = error.errorText === '' ? 'no further detail' : error.errorText;

  return `${error.url} could not be used — ${explainCandidateError(error.errorCode)} (code ${error.errorCode.toString()}: ${detail}).`;
}

function explainCandidateError(code: number): string {
  // 700 and above are the browser's own complaints; below that the number is a STUN
  // error response, and so came from the server itself.
  if (code === 701) {
    return 'it did not reply before the timeout, so it is unreachable or something is dropping the traffic';
  }
  if (code >= 700) return 'the browser could not make the request';
  if (code === 401 || code === 403) return 'it rejected the credentials';
  return 'it answered with an error';
}

/** How many routes of each kind were found, as a phrase to append to a sentence. */
export function summariseCandidates(types: readonly (string | null)[]): string {
  const count = (wanted: string): string =>
    types.filter((type) => type === wanted).length.toString();

  return `${count('host')} local, ${count('srflx')} via STUN, ${count('relay')} via a relay`;
}

/**
 * The warning that belongs with a gathering that found nothing but local addresses.
 *
 * `undefined` when there is nothing to say, so the caller can log unconditionally.
 */
export function reachabilityNote(types: readonly (string | null)[]): string | undefined {
  if (types.some((type) => type === 'srflx' || type === 'relay')) return undefined;

  return (
    'Only addresses local to this machine were found, so sharing can reach a browser ' +
    'on this network and nowhere else. Either no STUN server is configured or the one ' +
    'that is could not be reached.'
  );
}

/** How a connection that worked was actually made — the thing NAT problems turn on. */
export function describeRoute(route: Route): string {
  return `${candidateTypeName(route.local)} at this end and ${candidateTypeName(route.remote)} at the other`;
}

function candidateTypeName(type: string): string {
  switch (type) {
    case 'host':
      return 'a local address';
    case 'srflx':
      return 'an address found by STUN';
    case 'prflx':
      return 'an address learned from the peer';
    case 'relay':
      return 'a relayed address';
    default:
      return `an address of type "${type}"`;
  }
}

/**
 * Dig the winning pair out of a statistics report.
 *
 * Takes loose records rather than a `RTCStatsReport` so it can be tested without one, and
 * because the report's own typing is `any` throughout — checking each field is the only
 * way to be sure of what came back.
 *
 * A nominated pair is preferred: a connection can report more than one succeeded pair
 * while it is still deciding, and the nominated one is the decision.
 */
export function selectedRoute(entries: readonly unknown[]): Route | undefined {
  const byId = new Map<string, Record<string, unknown>>();
  for (const entry of entries) {
    if (!isRecord(entry)) continue;
    const id = entry.id;
    if (typeof id === 'string') byId.set(id, entry);
  }

  let fallback: Route | undefined;

  for (const entry of byId.values()) {
    if (entry.type !== 'candidate-pair' || entry.state !== 'succeeded') continue;

    const local = candidateTypeOf(byId, entry.localCandidateId);
    const remote = candidateTypeOf(byId, entry.remoteCandidateId);
    if (local === undefined || remote === undefined) continue;

    if (entry.nominated === true) return { local, remote };
    fallback ??= { local, remote };
  }

  return fallback;
}

function candidateTypeOf(
  byId: ReadonlyMap<string, Record<string, unknown>>,
  id: unknown,
): string | undefined {
  if (typeof id !== 'string') return undefined;
  const candidateType = byId.get(id)?.candidateType;
  return typeof candidateType === 'string' ? candidateType : undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
